/**
 * Client-side unit conversion for recipe ingredients.
 * Supports weight (g, kg, oz, lb) and volume (ml, l, tsp, tbsp, floz, cup).
 * Cross-dimension conversions assume water-like density (1 g == 1 ml) as a
 * best-effort fallback — the operator can always fall back to "As written".
 */

const WEIGHT_G  = { g: 1, kg: 1000, oz: 28.3495, lb: 453.592 };
const VOLUME_ML = { ml: 1, l: 1000, tsp: 4.92892, tbsp: 14.7868, floz: 29.5735, cup: 236.588 };

const UNIT_ALIASES = {
  g: 'g', gram: 'g', grams: 'g',
  kg: 'kg', kilogram: 'kg', kilograms: 'kg',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  ml: 'ml', milliliter: 'ml', milliliters: 'ml', millilitre: 'ml', millilitres: 'ml',
  l: 'l', liter: 'l', liters: 'l', litre: 'l', litres: 'l',
  tsp: 'tsp', teaspoon: 'tsp', teaspoons: 'tsp',
  tbsp: 'tbsp', tablespoon: 'tbsp', tablespoons: 'tbsp', tbs: 'tbsp',
  cup: 'cup', cups: 'cup',
  floz: 'floz', 'fl oz': 'floz', 'fluid ounce': 'floz', 'fluid ounces': 'floz',
};

export const UNIT_LABELS = {
  g: 'g', kg: 'kg', oz: 'oz', lb: 'lb',
  ml: 'ml', l: 'l', tsp: 'tsp', tbsp: 'tbsp', floz: 'fl oz', cup: 'cup',
};

export const COMPATIBLE_UNITS = {
  weight: ['g', 'kg', 'oz', 'lb'],
  volume: ['ml', 'l', 'tsp', 'tbsp', 'floz', 'cup'],
};

// Flat list surfaced to the per-row picker so an operator can swap ANY
// unit into ANY row — grams into teaspoons, cups into ounces — using
// the water-density bridge inside convertQty(). Order is kitchen-natural:
// small weight → big weight → small volume → big volume.
export const ALL_KITCHEN_UNITS = [
  'g', 'kg', 'oz', 'lb',
  'tsp', 'tbsp', 'floz', 'cup', 'ml', 'l',
];

export const UNIT_FAMILIES = [
  { key: 'as-written', label: 'As written (AI defaults)' },
  { key: 'metric',     label: 'Metric (g / kg / ml / l)' },
  { key: 'us',         label: 'US customary (oz / lb / tsp / tbsp / cup)' },
];

export function canonicalUnit(u) {
  if (!u) return '';
  const k = String(u).toLowerCase().replace(/\./g, '').trim();
  return UNIT_ALIASES[k] || k;
}

export function unitDimension(u) {
  const c = canonicalUnit(u);
  if (c in WEIGHT_G)  return 'weight';
  if (c in VOLUME_ML) return 'volume';
  return null;
}

function roundNice(n) {
  if (!Number.isFinite(n)) return n;
  if (n >= 100) return Math.round(n);
  if (n >= 10)  return Number(n.toFixed(1));
  if (n >= 1)   return Number(n.toFixed(2));
  return Number(n.toFixed(3));
}

// Convert `qty fromUnit` into `toUnit`. Falls back to same-value passthrough
// when either unit is unknown (e.g. "whole", "clove", "pinch"). Cross-
// dimension conversions bridge on water density (1 g == 1 ml).
export function convertQty(qty, fromUnit, toUnit) {
  if (!Number.isFinite(qty)) return { quantity: qty, unit: toUnit || fromUnit };
  const fromC = canonicalUnit(fromUnit);
  const toC   = canonicalUnit(toUnit);
  if (fromC === toC) return { quantity: qty, unit: toC };
  const fromDim = unitDimension(fromC);
  const toDim   = unitDimension(toC);
  if (!fromDim || !toDim) return { quantity: qty, unit: fromUnit };
  const fromTable = fromDim === 'weight' ? WEIGHT_G : VOLUME_ML;
  const toTable   = toDim   === 'weight' ? WEIGHT_G : VOLUME_ML;
  const canonical = qty * fromTable[fromC]; // in grams OR millilitres
  // Bridge grams<->ml assuming water density.
  const bridged   = canonical; // 1 g water == 1 ml water
  const out       = bridged / toTable[toC];
  return { quantity: roundNice(out), unit: toC };
}

// Pick the "best" unit inside a family (metric | us) for a given canonical
// magnitude so the operator sees natural units (500 g instead of 0.5 kg).
export function autoUnit(qty, fromUnit, family) {
  if (family === 'as-written' || !family) return { quantity: qty, unit: fromUnit };
  const dim = unitDimension(fromUnit);
  if (!dim) return { quantity: qty, unit: fromUnit };
  const fromC = canonicalUnit(fromUnit);
  const table = dim === 'weight' ? WEIGHT_G : VOLUME_ML;
  const canonical = qty * (table[fromC] || 1);
  let target;
  if (family === 'metric') {
    if (dim === 'weight') target = canonical >= 1000 ? 'kg' : 'g';
    else                  target = canonical >= 1000 ? 'l'  : 'ml';
  } else {
    // us — pick the unit a US home cook would actually reach for. Small
    // weights (spices, salt) collapse to tsp/tbsp via the water bridge
    // because "0.07 oz thyme" is useless in a kitchen; "0.5 tsp" is not.
    if (dim === 'weight') {
      if      (canonical >= 453.592) target = 'lb';
      else if (canonical >= 60)      target = 'oz';   // ~2 oz+
      else if (canonical >= 15)      target = 'tbsp'; // 1 tbsp ≈ 15 g water
      else                            target = 'tsp';  // 1 tsp ≈ 5 g water
    } else {
      if (canonical >= 236.588)      target = 'cup';
      else if (canonical >= 29.5735) target = 'tbsp';
      else                            target = 'tsp';
    }
  }
  return convertQty(qty, fromUnit, target);
}
